import { Timestamp } from "firebase/firestore";

/**
 * CTribe OKR (Objectives & Key Results) System
 * Data models for Goals, Key Results, and Weekly Check-ins
 */

// ============================================================================
// Enums & Constants
// ============================================================================

export type GoalStatus = "on_track" | "at_risk" | "behind" | "completed";

export type CheckinMood = "great" | "good" | "okay" | "struggling";

export const GOAL_STATUS_COLORS: Record<GoalStatus, string> = {
  on_track: "#22c55e", // green-500
  at_risk: "#f59e0b", // amber-500
  behind: "#ef4444", // red-500
  completed: "#3b82f6", // blue-500
};

export const GOAL_STATUS_LABELS: Record<GoalStatus, string> = {
  on_track: "On Track",
  at_risk: "At Risk",
  behind: "Behind",
  completed: "Completed",
};

export const CHECKIN_MOOD_COLORS: Record<CheckinMood, string> = {
  great: "#22c55e", // green-500
  good: "#3b82f6", // blue-500
  okay: "#f59e0b", // amber-500
  struggling: "#ef4444", // red-500
};

export const CHECKIN_MOOD_LABELS: Record<CheckinMood, string> = {
  great: "Great",
  good: "Good",
  okay: "Okay",
  struggling: "Struggling",
};

export const CHECKIN_MOOD_EMOJIS: Record<CheckinMood, string> = {
  great: "🚀",
  good: "😊",
  okay: "😐",
  struggling: "😓",
};

// ============================================================================
// Goal (Collection: kanban_workspaces/{workspaceId}/goals)
// ============================================================================

export interface Goal {
  id: string;
  workspaceId: string;
  title: string;
  description?: string;

  // Owner
  ownerId: string;
  ownerName: string;
  ownerPhotoUrl?: string;

  // Timeline
  startDate: Timestamp;
  endDate: Timestamp; // Quarter end

  // Progress
  progress: number; // 0-100, calculated from KRs
  status: GoalStatus;

  // Metadata
  createdAt: Timestamp;
  createdBy: string;
  updatedAt: Timestamp;
}

// ============================================================================
// Key Result (Collection: kanban_workspaces/{workspaceId}/goals/{goalId}/key_results)
// ============================================================================

export interface KeyResult {
  id: string;
  goalId: string;
  workspaceId: string;
  title: string;

  // Measurement
  targetValue: number;
  currentValue: number;
  startValue: number; // Initial value for baseline
  unit: string; // e.g., "%", "tasks", "users", "$"

  // Progress (calculated: (currentValue - startValue) / (targetValue - startValue) * 100)
  progress: number; // 0-100

  // Task linking
  linkedTaskIds: string[]; // Tasks that contribute to this KR

  // Metadata
  createdAt: Timestamp;
  createdBy: string;
  updatedAt: Timestamp;
}

// ============================================================================
// Check-in Task Reference (linking tasks to check-ins)
// ============================================================================

export interface CheckinTaskReference {
  taskId: string;
  taskTitle: string;          // Denormalized snapshot
  taskStatusId: string;       // Status at time of check-in
  note?: string;              // User's note for this task in this check-in
  category: "accomplished" | "blocked" | "planned";
}

// ============================================================================
// Weekly Check-in (Collection: kanban_workspaces/{workspaceId}/checkins)
// ============================================================================

export interface GoalProgressUpdate {
  goalId: string;
  goalTitle: string;
  update?: string;
  updateText?: string;
  previousProgress: number;
  currentProgress: number;
}

export interface WeeklyCheckin {
  id: string;
  workspaceId: string;
  userId: string;
  userName: string;
  userPhotoUrl?: string;

  // Week identifier (start of week - Monday)
  weekStartDate: Timestamp;

  // Check-in content
  accomplishments: string[]; // What did you accomplish?
  blockers: string[]; // What's blocking you?
  plans: string[]; // What will you do next week?
  mood: CheckinMood;

  // Optional: Additional notes
  notes?: string;

  // Optional linked items
  completedTaskIds: string[];
  goalProgress: GoalProgressUpdate[];
  linkedTasks?: CheckinTaskReference[]; // Structured task links

  // Metadata
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// ============================================================================
// API Request/Response Types
// ============================================================================

export interface CreateGoalRequest {
  workspaceId: string;
  title: string;
  description?: string;
  ownerId: string;
  ownerName: string;
  ownerPhotoUrl?: string;
  startDate: string; // ISO string
  endDate: string; // ISO string
}

export interface UpdateGoalRequest {
  goalId: string;
  workspaceId: string;
  title?: string;
  description?: string;
  ownerId?: string;
  ownerName?: string;
  ownerPhotoUrl?: string;
  startDate?: string; // ISO string
  endDate?: string; // ISO string
  status?: GoalStatus;
}

export interface CreateKeyResultRequest {
  workspaceId: string;
  goalId: string;
  title: string;
  targetValue: number;
  startValue?: number;
  currentValue?: number;
  unit: string;
}

export interface UpdateKeyResultRequest {
  workspaceId: string;
  goalId: string;
  keyResultId: string;
  title?: string;
  targetValue?: number;
  currentValue?: number;
  unit?: string;
  linkedTaskIds?: string[];
}

export interface CreateCheckinRequest {
  workspaceId: string;
  userId: string;
  userName: string;
  userPhotoUrl?: string;
  weekStartDate: string; // ISO string (Monday of the week)
  accomplishments: string[];
  blockers: string[];
  plans: string[];
  mood: CheckinMood;
  notes?: string;
  completedTaskIds?: string[];
  goalProgress?: GoalProgressUpdate[];
  linkedTasks?: CheckinTaskReference[];
}

export interface UpdateCheckinRequest {
  workspaceId: string;
  checkinId: string;
  accomplishments?: string[];
  blockers?: string[];
  plans?: string[];
  mood?: CheckinMood;
  notes?: string;
  completedTaskIds?: string[];
  goalProgress?: GoalProgressUpdate[];
  linkedTasks?: CheckinTaskReference[];
}

// ============================================================================
// Query/Filter Types
// ============================================================================

export interface GoalQueryFilters {
  workspaceId: string;
  ownerId?: string;
  status?: GoalStatus;
  startDateAfter?: string; // ISO string
  endDateBefore?: string; // ISO string
}

export interface CheckinQueryFilters {
  workspaceId: string;
  userId?: string;
  weekStartDate?: string; // ISO string
  startDate?: string; // ISO string (range start)
  endDate?: string; // ISO string (range end)
}

// ============================================================================
// Dashboard / Summary Types
// ============================================================================

export interface GoalSummary {
  total: number;
  byStatus: Record<GoalStatus, number>;
  averageProgress: number;
}

export interface CheckinSummary {
  totalCheckins: number;
  thisWeekCount: number;
  moodDistribution: Record<CheckinMood, number>;
  participationRate: number; // percentage of team that checked in
}

export interface WorkspaceOKRSummary {
  goals: GoalSummary;
  checkins: CheckinSummary;
  recentActivity: {
    goalsUpdated: number;
    keyResultsUpdated: number;
    checkinsSubmitted: number;
  };
}

// ============================================================================
// Helper Functions
// ============================================================================

export function calculateKeyResultProgress(kr: KeyResult): number {
  if (kr.targetValue === kr.startValue) return 0;
  const progress = ((kr.currentValue - kr.startValue) / (kr.targetValue - kr.startValue)) * 100;
  return Math.max(0, Math.min(100, Math.round(progress)));
}

export function calculateGoalProgress(keyResults: KeyResult[]): number {
  if (keyResults.length === 0) return 0;
  const totalProgress = keyResults.reduce((sum, kr) => sum + kr.progress, 0);
  return Math.round(totalProgress / keyResults.length);
}

export function getGoalStatusFromProgress(progress: number, endDate: Timestamp): GoalStatus {
  const now = new Date();
  const end = endDate.toDate();

  if (progress >= 100) return "completed";

  // Calculate expected progress based on time elapsed.
  // NOTE: Assumes quarterly goals (3-month window). Consider making this configurable per workspace.
  const start = new Date(end);
  start.setMonth(start.getMonth() - 3); // Assume quarterly goals

  const totalDuration = end.getTime() - start.getTime();
  const elapsed = now.getTime() - start.getTime();
  const expectedProgress = (elapsed / totalDuration) * 100;

  if (progress >= expectedProgress - 10) return "on_track";
  if (progress >= expectedProgress - 25) return "at_risk";
  return "behind";
}

export function getWeekStartDate(date: Date = new Date()): Date {
  const result = new Date(date);
  const day = result.getDay();
  const diff = result.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Monday start
  result.setDate(diff);
  result.setHours(0, 0, 0, 0);
  return result;
}

export function formatWeekRange(weekStart: Date): string {
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);

  const options: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
  const startStr = weekStart.toLocaleDateString("en-US", options);
  const endStr = weekEnd.toLocaleDateString("en-US", options);

  return `${startStr} - ${endStr}`;
}

export function isValidGoalStatus(value: string): value is GoalStatus {
  return ["on_track", "at_risk", "behind", "completed"].includes(value);
}

export function isValidCheckinMood(value: string): value is CheckinMood {
  return ["great", "good", "okay", "struggling"].includes(value);
}

/**
 * Safely convert a Firestore Timestamp (or serialized timestamp object) to a Date.
 * When data passes through Next.js server actions, Timestamps are serialized to plain
 * objects like { seconds: number, nanoseconds: number } and lose their .toDate() method.
 * This helper handles both cases.
 */
export function timestampToDate(
  timestamp: Timestamp | { seconds: number; nanoseconds: number } | Date | null | undefined
): Date {
  if (!timestamp) {
    return new Date();
  }

  // If it's already a Date
  if (timestamp instanceof Date) {
    return timestamp;
  }

  // If it has a toDate method (actual Firestore Timestamp)
  if (typeof (timestamp as Timestamp).toDate === "function") {
    return (timestamp as Timestamp).toDate();
  }

  // If it's a serialized timestamp object with seconds
  if ("seconds" in timestamp && typeof timestamp.seconds === "number") {
    return new Date(timestamp.seconds * 1000);
  }

  // Fallback
  return new Date();
}

// ============================================================================
// Clawdbot OKR Request/Response Types
// ============================================================================

export type ClawdbotOKRIntent =
  | "goal.create"
  | "goal.list"
  | "goal.update"
  | "kr.update"
  | "checkin.submit"
  | "checkin.list"
  | "summary";

export interface ClawdbotOKRRequest {
  intent: ClawdbotOKRIntent;
  entities: {
    workspaceSlug?: string;
    goalId?: string;
    goalTitle?: string;
    goalDescription?: string;
    goalOwnerId?: string;
    goalOwnerName?: string;
    startDate?: string; // ISO string
    endDate?: string; // ISO string
    keyResultId?: string;
    krTitle?: string;
    krCurrentValue?: number;
    krTargetValue?: number;
    krUnit?: string;
    // Check-in fields
    accomplishments?: string[];
    blockers?: string[];
    plans?: string[];
    mood?: CheckinMood;
    weekStartDate?: string; // ISO string
    notes?: string;
    completedTaskIds?: string[];
    linkedTasks?: CheckinTaskReference[];
  };
  userId: string;
  userDisplayName: string;
  channel: "discord" | "slack" | "api";
  channelUserId?: string;
}

export interface ClawdbotOKRResponse {
  success: boolean;
  message: string;
  data?: {
    goal?: Goal;
    goals?: Goal[];
    keyResult?: KeyResult;
    keyResults?: KeyResult[];
    checkin?: WeeklyCheckin;
    checkins?: WeeklyCheckin[];
    summary?: GoalSummary;
  };
  error?: string;
}
